import { AfterViewInit, Directive, inject, Input, OnDestroy } from "@angular/core";
import { EMPTY, from, Observable, ReplaySubject, Subscription } from "rxjs";
import { catchError, concatMap, filter, switchMap, takeUntil, timeout } from "rxjs/operators";
import { PCP_LOGGER } from '@pcp/logging';
import { VideoPlayerComponent } from "../components/video-player/video-player.component";
import { CommandType } from "../interfaces/streaming";
import { PlayVideoCommand, SeekVideoCommand, VideoCommand } from "../interfaces/video-commands";

/**
 * Attaches to the StreamingHub's video related events
 * and delegates them to the player.
 */
@Directive({ selector: '[pcpConsumingPlayer]' })
export class ConsumingPlayerDirective implements AfterViewInit, OnDestroy {

    private readonly log = inject(PCP_LOGGER).createScoped('signalr');
    private readonly round = Math.round;
    private readonly afterViewInit$ = new ReplaySubject<void>();
    private readonly onDestory$ = new ReplaySubject<void>();


    private subscription?: Subscription;
    private readonly videoCommands = [
        CommandType.LOAD,
        CommandType.PLAY,
        CommandType.PAUSE,
        CommandType.SEEK,
    ];

    constructor(
        private player: VideoPlayerComponent,
    ) {
    }

    @Input() set videoCommandStream$(stream$: undefined | Observable<VideoCommand>) {
        this.subscription?.unsubscribe();
        if (!stream$) {
            return;
        }

        this.subscription = this.afterViewInit$.pipe(
            switchMap(() => stream$),
            filter(command => this.videoCommands.includes(command.commandType)),
            // tap(() => this.playerConfig.setPauseEmitVideoChanges(true)),

            concatMap(async command => {
                // this.player.removePlayerEvents();
                try {
                    switch (command.commandType) {
                        case CommandType.LOAD:
                            break;
                        case CommandType.PLAY:
                            try {
                                const playCmd = command as PlayVideoCommand;
                                // TODO: maybe better to _not_ round?
                                //       If rounding when consuming, it could be that e.g.
                                //       The beamer has seeked to 1:30
                                //       and the teacher has to seek to 1:00
                                const position = this.round(Number.parseFloat(playCmd.commandParam));
                                return from(this.player.playVideo(position)).pipe(
                                    timeout(200),
                                    catchError(err => {
                                        this.log.error('consumer: play did not finish', err instanceof Error ? err : undefined, { err });
                                        return EMPTY;
                                    }),
                                );
                            } catch (error) {
                                this.log.error('consumer: unexpected error during play', error instanceof Error ? error : undefined);
                                throw error;
                            }
                        case CommandType.PAUSE:
                            return from(this.player.pauseVideo()).pipe(
                                timeout(200),
                                catchError(err => {
                                    this.log.error('consumer: pauseVideo did not finish', err instanceof Error ? err : undefined, { err });
                                    return EMPTY;
                                }),
                            );
                        case CommandType.SEEK: {
                            // `isInPausedState` describes the paused state of the sender (teacher)
                            // if the sender is in another paused/playing state,
                            // adjust the consuming player's state accordingly.
                            const { seconds, isInPausedState } = command as SeekVideoCommand;
                            // TODO: round or not round?
                            // await this.player.seekTo(this.round(seconds), isInPausedState);
                            return from(this.player.seekTo(seconds, isInPausedState)).pipe(
                                timeout(200),
                                catchError(err => {
                                    this.log.error('consumer: seekTo did not finish', err instanceof Error ? err : undefined, { err });
                                    return EMPTY;
                                }),
                            );
                        }
                    }
                    // this.player.insertPlayerEventListeners();
                    return Promise.resolve();
                } catch (e) {
                    this.log.error('Unexpected error processing video command', e instanceof Error ? e : undefined);
                    return Promise.reject(e);
                }
            }),
            // tap(() => this.playerConfig.setPauseEmitVideoChanges(false)),
            catchError(error => {
                this.log.error('Error while processing VideoCommand', error instanceof Error ? error : undefined, { error });
                return EMPTY;
            }),
            takeUntil(this.onDestory$),
        ).subscribe();
    }

    ngAfterViewInit(): void {
        this.afterViewInit$.next();
        this.afterViewInit$.complete();
    }

    ngOnDestroy(): void {
        this.onDestory$.next();
        this.onDestory$.complete();
        this.subscription?.unsubscribe();
    }
}
